/**
 * Environment selection utilities
 * Based on legacy choco-admin-portal system
 */

import {
  ENVIRONMENT_CONFIGS,
  DEFAULT_ENVIRONMENT,
  PRODUCTION_ENVIRONMENT,
} from './config';
import { EnvironmentKey, EnvironmentConfig } from './types';

// Local storage key for selected environment
export const LS_SELECTED_ENV = 'ChocoAdminSelectedEnvironment';

/**
 * Check if we're running in production mode
 */
export const isProduction = (): boolean => {
  return process.env.NODE_ENV === 'production';
};

/**
 * Check if environment is valid
 */
export const isValidEnvironment = (
  environment: string
): environment is EnvironmentKey => {
  return environment in ENVIRONMENT_CONFIGS;
};

/**
 * Get environment from localStorage or default.
 *
 * Falls back to the default environment when the stored key is unknown, which
 * is what a selection persisted before a config was removed looks like (see
 * config.ts). Resolving a removed key would initialize auth against AWS
 * resources that no longer exist, which strips the Authorization header from
 * every GraphQL request.
 */
export const getSelectedEnvironment = (): EnvironmentKey => {
  if (isProduction()) {
    return PRODUCTION_ENVIRONMENT;
  }

  try {
    const stored = localStorage.getItem(LS_SELECTED_ENV);
    if (stored && isValidEnvironment(stored)) {
      return stored;
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Failed to read environment from localStorage:', error);
  }

  return DEFAULT_ENVIRONMENT;
};

/**
 * Set selected environment in localStorage
 */
export const setSelectedEnvironment = (environment: EnvironmentKey): void => {
  if (isProduction()) {
    // eslint-disable-next-line no-console
    console.warn('Cannot change environment in production');
    return;
  }

  try {
    localStorage.setItem(LS_SELECTED_ENV, environment);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to save environment to localStorage:', error);
  }
};

/**
 * Get configuration for specific environment
 */
export const getEnvironmentConfig = (
  environment: EnvironmentKey
): EnvironmentConfig => {
  const config = ENVIRONMENT_CONFIGS[environment];
  if (!config) {
    // eslint-disable-next-line no-console
    console.error(`Environment configuration not found for: ${environment}`);
    return ENVIRONMENT_CONFIGS[DEFAULT_ENVIRONMENT];
  }
  return config;
};

/**
 * Get current environment configuration
 */
export const getCurrentEnvironmentConfig = (): EnvironmentConfig => {
  return getEnvironmentConfig(getSelectedEnvironment());
};

/**
 * Get all available environments (excluding production in non-production builds)
 */
export const getAvailableEnvironments = (): EnvironmentKey[] => {
  const allEnvironments = Object.keys(ENVIRONMENT_CONFIGS) as EnvironmentKey[];

  if (isProduction()) {
    return [PRODUCTION_ENVIRONMENT];
  }

  // In development, show all environments except production
  return allEnvironments.filter((env) => env !== PRODUCTION_ENVIRONMENT);
};

/**
 * Get environment display name with emoji
 */
export const getEnvironmentDisplayName = (
  environment: EnvironmentKey
): string => {
  const config = getEnvironmentConfig(environment);
  return `${config.emoji} ${config.name}`;
};

/**
 * Clear stored environment (useful for logout)
 */
export const clearStoredEnvironment = (): void => {
  try {
    localStorage.removeItem(LS_SELECTED_ENV);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Failed to clear stored environment:', error);
  }
};

/**
 * Get environment-specific connection error message
 */
export const getEnvironmentConnectionErrorMessage = (
  environment: EnvironmentKey
): string => {
  const config = getEnvironmentConfig(environment);
  return `Failed to connect to ${config.emoji} ${config.name} environment. Please check your connection and try again.`;
};
